import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, Modal, Switch } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { endOfDayIso, startOfDayIso } from '@/shared/lib/format'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  createEmployee,
  isValidEmployeeNo,
  updateEmployee,
  type AttendanceEmployee,
} from './attendanceApi'

interface EmployeeFormModalProps {
  open: boolean
  /** `null` = yangi xodim */
  employee: AttendanceEmployee | null
  onClose: () => void
}

interface FormValues {
  employeeNo: string
  fullName: string
  /** ⚠️ Ikkalasi BITTA maydonda — quyidagi izohga qarang */
  validity?: [Dayjs | null, Dayjs | null] | null
  active: boolean
}

/**
 * Davomat xodimini qo'shish va tahrirlash.
 *
 * <h2>⚠️ TABEL RAQAMI TAHRIRLASHDA QULFLANGAN</h2>
 * `DeviceFormModal` dagi `deviceId` bilan aynan bir xil qaror, lekin bu
 * yerda oqibati og'irroq. Tabel raqami uch joyda kalit:
 * qurilmada (`employeeNoString`), hodisalarda (`event.employee_no`, MATN,
 * FK'siz) va hisobotda (xodim × kun kesimi shu ustun bo'yicha yig'iladi).
 * Uni almashtirish qurilmadagi eski yozuvni yetim qoldirardi va butun
 * o'tmish davomat tarixi endi mavjud bo'lmagan odamga tegishli bo'lib
 * qolardi — jimgina, xatosiz.
 *
 * Maydon YASHIRILMAYDI, `disabled` qilinadi: butunlay olib tashlash
 * "raqam yo'q ekan" degan taassurot berardi.
 *
 * <h2>⚠️ FAQAT RAQAM, 32 BELGIGACHA</h2>
 * Bu bizning injiqligimiz emas — Hikvision boshqasini rad etadi va sabab
 * tushunarsiz `subStatusCode` bo'lib qaytadi. Cheklov bazada ham bor
 * (`employee_no_numeric`), ya'ni server baribir rad etardi; bu yerdagi
 * tekshiruv faqat XABARNI tushunarli qiladi.
 *
 * <h2>⚠️ AMAL MUDDATI — QURILMADAGI CHEKLOV</h2>
 * `validFrom`/`validTo` qurilmaga `UserInfo.Valid` bo'lib uzatiladi, ya'ni
 * muddat tugagach eshik O'ZI ochilmay qoladi. Bu panel ko'rsatkichi emas.
 * Shuning uchun forma buni ochiq aytadi: bu maydon xodimning ishga qabul
 * sanasi emas, JISMONIY kirish huquqining muddati.
 */
export function EmployeeFormModal({ open, employee, onClose }: EmployeeFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      employee
        ? {
            employeeNo: employee.employeeNo,
            fullName: employee.fullName,
            validity: [
              employee.validFrom ? dayjs(employee.validFrom) : null,
              employee.validTo ? dayjs(employee.validTo) : null,
            ],
            active: employee.active,
          }
        : // ⚠️ `[null, null]`, `null` EMAS: antd `setFieldsValue` tipi
          // oraliq maydonida massiv kutadi va `null` ni qabul qilmaydi.
          // Ikkala cheti bo'sh oraliq — "muddat qo'yilmagan" ning aynan o'zi.
          { employeeNo: '', fullName: '', validity: [null, null], active: true },
    )
  }, [open, employee, form])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      /*
        ⚠️ ORALIQ BITTA MAYDONDA (`RangePicker`), ikkita alohida sana
        tanlagich EMAS. Sabab bazadagi cheklovda: `employee_valid_range`
        `valid_to > valid_from` ni talab qiladi. Ikki mustaqil maydon
        bilan operator teskari oraliq kiritishi va faqat saqlagandan keyin
        buni bilishi mumkin edi — `RangePicker` esa ikkinchi sanani
        birinchisidan oldin tanlashga umuman yo'l qo'ymaydi.

        ⚠️ CHEKLANMAGAN MUDDAT — HAQIQIY va ODATIY holat: doimiy xodimda
        muddat bo'lmaydi. `null` yuboriladi, "juda uzoq sana" EMAS:
        2099-yil qo'yish bazada cheklov bordek ko'rsatardi va u kunlarnidir
        haqiqatan ishga tushardi.
      */
      const from = values.validity?.[0] ?? null
      const to = values.validity?.[1] ?? null
      const common = {
        fullName: values.fullName.trim(),
        // ⚠️ Kun chegarasi TENANT zonasida (`dayRange.ts`). Ilgari
        // `startOf`/`endOf` brauzernikini olardi va qurilmaga
        // (`UserInfo.Valid`) yarim soat siljigan muddat ketardi —
        // eshik kutilganidan erta yoki kech yopilardi.
        validFrom: from ? startOfDayIso(from.format('YYYY-MM-DD')) : null,
        validTo: to ? endOfDayIso(to.format('YYYY-MM-DD')) : null,
      }

      if (employee) {
        // ⚠️ `employeeNo` YUBORILMAYDI — `EmployeeUpdate` da bunday maydon
        // yo'q (sabab komponent sarlavhasida).
        //
        // ⚠️ `personRef` esa YUBORILADI, garchi formada ko'rinmasa ham:
        // `PUT` to'liq almashtirish va ustun `COALESCE` siz yoziladi,
        // ya'ni yubormaslik HR bog'lanishini jimgina o'chirardi.
        return updateEmployee(employee.id, {
          ...common,
          active: values.active,
          personRef: employee.personRef,
        })
      }
      // ⚠️ Yaratishda `active` YUBORILMAYDI: yangi xodim har doim faol
      // (`employee.active DEFAULT TRUE`).
      return createEmployee({ ...common, employeeNo: values.employeeNo.trim() })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.attendance.all })
      form.resetFields()
      onClose()
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={employee ? t('attendance.employee.editTitle') : t('attendance.employee.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            employee ? t('attendance.employee.editNote') : t('attendance.employee.createNote')
          }
        />

        <Form.Item
          name="employeeNo"
          label={t('attendance.employee.employeeNo')}
          extra={
            employee
              ? t('attendance.employee.employeeNoLockedHint')
              : t('attendance.employee.employeeNoHint')
          }
          rules={
            employee
              ? []
              : [
                  {
                    required: true,
                    message: t('attendance.employee.validation.employeeNoRequired'),
                  },
                  {
                    validator: (_, value: string | undefined) =>
                      !value || isValidEmployeeNo(value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(t('attendance.employee.validation.employeeNoFormat')),
                          ),
                  },
                ]
          }
        >
          <Input autoFocus placeholder="1001" disabled={employee !== null} />
        </Form.Item>

        <Form.Item
          name="fullName"
          label={t('attendance.employee.fullName')}
          extra={t('attendance.employee.fullNameHint')}
          rules={[{ required: true, message: t('attendance.employee.validation.fullNameRequired') }]}
        >
          <Input placeholder={t('attendance.employee.fullNamePlaceholder')} />
        </Form.Item>

        <Form.Item
          name="validity"
          label={t('attendance.employee.validity')}
          extra={t('attendance.employee.validityHint')}
        >
          {/* `allowEmpty` IKKALA CHETDA ham: muddat "shu kundan boshlab,
              cheksiz" yoki "shu kungacha" bo'lishi mumkin va ikkalasi ham
              amaliyotda uchraydi (yangi xodim / vaqtinchalik ruxsat) */}
          <DatePicker.RangePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            allowEmpty={[true, true]}
          />
        </Form.Item>

        {/*
          ⚠️ "Faol" — o'chirish EMAS va farqi jiddiy. Nofaol xodim
          qurilmalardan OLIB TASHLANADI (navbatga `DELETE` tushadi), lekin
          uning hodisalar tarixi joyida qoladi. O'chirish esa xodimni
          reyestrdan yo'q qiladi — hisobotdagi eski yozuvlar esa `employee_no`
          bo'yicha o'qilishda davom etadi, chunki u MATN va FK'siz.

          ⚠️ FAQAT TAHRIRLASHDA: `EmployeeDraft` da bunday maydon yo'q.
        */}
        {employee === null ? null : (
          <Form.Item
            name="active"
            label={t('attendance.employee.active')}
            valuePropName="checked"
            extra={t('attendance.employee.activeHint')}
          >
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  )
}
